import { parseDuration } from './utils';

const MAX_WRAPPER_DEPTH = 5;

function getText(el, tag) {
  const node = el.querySelector(tag);
  return node ? node.textContent.trim() : '';
}

function getAll(el, tag) {
  return Array.from(el.querySelectorAll(tag));
}

function parseTrackingEvents(el) {
  const map = {};
  getAll(el, 'Tracking').forEach((t) => {
    const event = t.getAttribute('event');
    if (!event) return;
    if (!map[event]) map[event] = [];
    map[event].push(t.textContent.trim());
  });
  return map;
}

function parseMediaFiles(linearEl) {
  return getAll(linearEl, 'MediaFile').map((mf) => ({
    id: mf.getAttribute('id') || '',
    fileURL: mf.textContent.trim(),
    deliveryType: mf.getAttribute('delivery') || 'progressive',
    mimeType: mf.getAttribute('type') || '',
    bitrate: parseInt(mf.getAttribute('bitrate') || '0', 10),
    width: parseInt(mf.getAttribute('width') || '0', 10),
    height: parseInt(mf.getAttribute('height') || '0', 10),
  }));
}

function parseSkipOffset(val, duration) {
  if (!val) return -1;
  if (val.includes('%')) return duration * (parseFloat(val) / 100);
  return parseDuration(val);
}

function parseIcons(linearEl) {
  return getAll(linearEl, 'Icon').map((icon) => ({
    program: icon.getAttribute('program') || '',
    height: parseInt(icon.getAttribute('height') || '0', 10),
    width: parseInt(icon.getAttribute('width') || '0', 10),
    xPosition: icon.getAttribute('xPosition') || 'left',
    yPosition: icon.getAttribute('yPosition') || 'top',
    staticResource: getText(icon, 'StaticResource') || null,
    htmlResource: getText(icon, 'HTMLResource') || null,
    iframeResource: getText(icon, 'IFrameResource') || null,
    iconClickThroughURLTemplate: getText(icon, 'IconClickThrough') || null,
    duration: icon.getAttribute('duration') || '-1',
  }));
}

function parseLinearCreative(creativeEl) {
  const linearEl = creativeEl.querySelector('Linear');
  if (!linearEl) return null;

  const duration = parseDuration(getText(linearEl, 'Duration'));
  const skipDelay = parseSkipOffset(linearEl.getAttribute('skipoffset'), duration);

  const videoClicksEl = linearEl.querySelector('VideoClicks');
  let videoClickThroughURLTemplate = null;
  const videoClickTrackingURLTemplates = [];
  if (videoClicksEl) {
    const ct = getText(videoClicksEl, 'ClickThrough');
    if (ct) videoClickThroughURLTemplate = { url: ct };
    getAll(videoClicksEl, 'ClickTracking').forEach((el) => {
      videoClickTrackingURLTemplates.push(el.textContent.trim());
    });
  }

  const trackingEventsEl = linearEl.querySelector('TrackingEvents');

  return {
    type: 'linear',
    id: creativeEl.getAttribute('id') || '',
    sequence: parseInt(creativeEl.getAttribute('sequence') || '0', 10) || null,
    duration,
    skipDelay,
    mediaFiles: parseMediaFiles(linearEl),
    trackingEvents: trackingEventsEl ? parseTrackingEvents(trackingEventsEl) : {},
    videoClickThroughURLTemplate,
    videoClickTrackingURLTemplates,
    icons: parseIcons(linearEl),
  };
}

function parseNonLinearCreative(creativeEl) {
  const nonLinearAdsEl = creativeEl.querySelector('NonLinearAds');
  if (!nonLinearAdsEl) return null;

  const trackingEventsEl = nonLinearAdsEl.querySelector('TrackingEvents');

  const variations = getAll(nonLinearAdsEl, 'NonLinear').map((nl) => {
    const minDur = nl.getAttribute('minSuggestedDuration');
    return {
      id: nl.getAttribute('id') || '',
      width: parseInt(nl.getAttribute('width') || '0', 10),
      height: parseInt(nl.getAttribute('height') || '0', 10),
      expandedWidth: nl.getAttribute('expandedWidth') || '',
      expandedHeight: nl.getAttribute('expandedHeight') || '',
      minSuggestedDuration: minDur ? parseDuration(minDur) : -1,
      staticResource: getText(nl, 'StaticResource') || null,
      htmlResource: getText(nl, 'HTMLResource') || null,
      iframeResource: getText(nl, 'IFrameResource') || null,
      nonlinearClickThroughURLTemplate: getText(nl, 'NonLinearClickThrough') || null,
      adSlotID: nl.getAttribute('adSlotID') || null,
    };
  });

  return {
    type: 'nonlinear',
    id: creativeEl.getAttribute('id') || '',
    variations,
    trackingEvents: trackingEventsEl ? parseTrackingEvents(trackingEventsEl) : {},
  };
}

function parseCompanionCreative(creativeEl) {
  const companionAdsEl = creativeEl.querySelector('CompanionAds');
  if (!companionAdsEl) return null;

  const variations = getAll(companionAdsEl, 'Companion').map((comp) => {
    const staticResources = getAll(comp, 'StaticResource').map((sr) => ({
      url: sr.textContent.trim(),
      creativeType: sr.getAttribute('creativeType') || '',
    }));
    const htmlResources = getAll(comp, 'HTMLResource').map((el) => el.textContent.trim());
    const iframeResources = getAll(comp, 'IFrameResource').map((el) => el.textContent.trim());
    const trackingEventsEl = comp.querySelector('TrackingEvents');

    return {
      id: comp.getAttribute('id') || '',
      width: parseInt(comp.getAttribute('width') || '0', 10),
      height: parseInt(comp.getAttribute('height') || '0', 10),
      expandedWidth: comp.getAttribute('expandedWidth') || '',
      expandedHeight: comp.getAttribute('expandedHeight') || '',
      staticResources,
      htmlResources,
      iframeResources,
      companionClickThroughURLTemplate: getText(comp, 'CompanionClickThrough') || null,
      adSlotID: comp.getAttribute('adSlotID') || null,
      trackingEvents: trackingEventsEl ? parseTrackingEvents(trackingEventsEl) : {},
    };
  });

  return {
    type: 'companion',
    id: creativeEl.getAttribute('id') || '',
    variations,
  };
}

function parseAdVerifications(parentEl) {
  return getAll(parentEl, 'Verification')
    .map((v) => ({
      vendor: v.getAttribute('vendor') || 'unknown',
      resource: getText(v, 'JavaScriptResource') || '',
    }))
    .filter((v) => v.resource);
}

function parseInlineAd(adEl, inlineEl) {
  const impressionUrls = getAll(inlineEl, 'Impression').map((el) => el.textContent.trim());
  const errorUrls = getAll(inlineEl, 'Error').map((el) => el.textContent.trim());

  const creatives = [];
  getAll(inlineEl, 'Creative').forEach((creativeEl) => {
    const linear = parseLinearCreative(creativeEl);
    if (linear) { creatives.push(linear); return; }
    const nonlinear = parseNonLinearCreative(creativeEl);
    if (nonlinear) { creatives.push(nonlinear); return; }
    const companion = parseCompanionCreative(creativeEl);
    if (companion) creatives.push(companion);
  });

  return {
    id: adEl.getAttribute('id') || '',
    sequence: parseInt(adEl.getAttribute('sequence') || '0', 10) || null,
    adSystem: getText(inlineEl, 'AdSystem'),
    adTitle: getText(inlineEl, 'AdTitle'),
    impressionUrls,
    errorUrls,
    creatives,
    adVerifications: parseAdVerifications(inlineEl),
  };
}

function fetchXML(url) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'document';
    xhr.timeout = 10000;
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseXML);
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Timeout'));
    xhr.send();
  });
}

async function parseVASTDocument(xmlDoc, depth = 0) {
  if (!xmlDoc) return [];

  const vastEl = xmlDoc.querySelector('VAST');
  if (!vastEl) return [];

  const ads = [];

  for (const adEl of getAll(vastEl, 'Ad')) {
    const inlineEl = adEl.querySelector('InLine');
    const wrapperEl = adEl.querySelector('Wrapper');

    if (inlineEl) {
      const ad = parseInlineAd(adEl, inlineEl);
      if (ad) ads.push(ad);
    } else if (wrapperEl && depth < MAX_WRAPPER_DEPTH) {
      const wrapperUri = getText(wrapperEl, 'VASTAdTagURI');
      if (!wrapperUri) continue;

      try {
        const wrappedDoc = await fetchXML(wrapperUri);
        const resolvedAds = await parseVASTDocument(wrappedDoc, depth + 1);

        // Merge wrapper-level impression/error URLs into each resolved ad, and the wrapper's
        // linear tracking and click tracking into its linear creative: the wrapping ad server
        // counts those beacons too.
        const wrapperImpressions = getAll(wrapperEl, 'Impression').map((el) => el.textContent.trim());
        const wrapperErrors = getAll(wrapperEl, 'Error').map((el) => el.textContent.trim());
        const wrapperLinearEl = wrapperEl.querySelector('Linear');
        const wrapperTrackingEl = wrapperLinearEl?.querySelector('TrackingEvents');
        const wrapperTracking = wrapperTrackingEl ? parseTrackingEvents(wrapperTrackingEl) : {};
        const wrapperClickTracking = wrapperLinearEl
          ? getAll(wrapperLinearEl, 'ClickTracking').map((el) => el.textContent.trim())
          : [];
        resolvedAds.forEach((ad) => {
          ad.impressionUrls = [...wrapperImpressions, ...ad.impressionUrls];
          ad.errorUrls = [...wrapperErrors, ...ad.errorUrls];
          ad.creatives.filter((c) => c.type === 'linear').forEach((linear) => {
            Object.entries(wrapperTracking).forEach(([event, urls]) => {
              linear.trackingEvents[event] = [...urls, ...(linear.trackingEvents[event] ?? [])];
            });
            linear.videoClickTrackingURLTemplates = [...wrapperClickTracking, ...linear.videoClickTrackingURLTemplates];
          });
        });

        ads.push(...resolvedAds);
      } catch (_) {
        // Wrapper resolution failed — skip this ad
      }
    }
  }

  return ads;
}

export class VASTClient {
  async get(url) {
    const xmlDoc = await fetchXML(url);
    const ads = await parseVASTDocument(xmlDoc);
    return { ads };
  }

  // Parse an already-fetched XML DOM document (used for inline VMAP VAST data)
  async parseVAST(xmlDoc) {
    const ads = await parseVASTDocument(xmlDoc);
    return { ads };
  }
}